import React, { useState, useEffect } from 'react';
import { TextField, Button, Grid, Container, Snackbar, Paper, FormControl, Select, MenuItem, InputLabel, Checkbox, FormControlLabel } from '@mui/material';
import MuiAlert from '@mui/material/Alert';
import apiService from '../components/ReasuableFuncations/api';

const Alert = React.forwardRef(function Alert(props, ref) {
  return <MuiAlert elevation={6} ref={ref} variant="filled" {...props} />;
});

const UserManagement = () => {
  const [userName, setUserName] = useState(''); 
  const [userId, setUserId] = useState(''); 
  const [userType, setUserType] = useState('');
  const [region, setRegion] = useState('');
  const [permissions, setPermissions] = useState({
    viewReports: false,
    editData: false,
    deleteData: false,
  });
  const [users, setUsers] = useState([]);
  const [openSnackbar, setOpenSnackbar] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');
  const [snackbarSeverity, setSnackbarSeverity] = useState('success');
  const [errors, setErrors] = useState({});
  const [memberStateRegion, setMemberStateRegion] = useState('');
  const [memberState, setmemberState] = useState('');
  const [availableStates, setAvailableStates] = useState([]);
  const [allUsers, setAllUsers] = useState([]); // Add this new state

  const regionStates = {
    COMESA: [
      "Burundi", "Comoros", "Democratic Republic of the Congo (DRC)", "Djibouti",
      "Egypt", "Eritrea", "Burundi", "Eswatini", "Ethiopia", "Kenya", "Libya",
      "Madagascar", "Malawi", "Mauritius", "Rwanda", "Seychelles", "Somalia",
      "South Sudan", "Sudan", "Zambia", "Zimbabwe", "Tanzania"
    ],
    EAC: ["Burundi", "Kenya", "Rwanda", "South Sudan", "Tanzania", "Uganda","Congo","Somalia"],
    IGAD: ["Djibouti", "Eritrea", "Ethiopia", "Kenya", "Somalia", "South Sudan", "Sudan", "Uganda"],
    IOC: ["Comoros", "France (Réunion)", "Madagascar", "Mauritius", "Seychelles"],
    SADC: [
      "Angola", "Botswana", "Comoros", "Democratic Republic of the Congo (DRC)",
      "Eswatini", "Lesotho", "Madagascar", "Malawi", "Mozambique", "Namibia",
      "Seychelles", "South Africa", "Tanzania", "Zambia", "Zimbabwe", "Tanzania"
    ],
  };

  const fetchUsers = async () => {
    try {
      const response = await apiService.get(`/api/get-all-users`);
      console.log('Fetched users:', response.data); // Debug log
      setAllUsers(response.data);
      setUsers(response.data);
    } catch (error) {
      console.error('Error fetching users:', error);
    }
  };

  useEffect(() => {
    fetchUsers();
  }, []);

  useEffect(() => {
    // Scroll to the top of the page when the component mounts
    window.scrollTo(0, 0);
  }, []);

  useEffect(() => {
    if (userType === 'Member State' && memberStateRegion) {
      setAvailableStates(regionStates[memberStateRegion] || []);
    } else {
      setAvailableStates([]);
    }
  }, [memberStateRegion, userType]);

  // Update the filtering useEffect
  useEffect(() => {
    if (!allUsers.length) return;

    let filteredUsers = [...allUsers];

    if (userType === 'Regional Network' && region) {
      console.log('Filtering Regional Network users for region:', region);
      filteredUsers = allUsers.filter(user => 
        user.selectedOption === 'Regional Network' && 
        user.regionalNetworkOption === region
      );
    } else if (userType === 'Member State' && memberStateRegion && memberState) {
      console.log('Filtering Member State users');
      filteredUsers = allUsers.filter(user => 
        user.selectedOption === 'Member State' && 
        user.memberStateOption === memberStateRegion &&
        user.countryOption === memberState
      );
    }

    console.log('Filtered users:', filteredUsers);
    setUsers(filteredUsers);
  }, [region, userType, memberStateRegion, memberState, allUsers]);

  const handleSaveUser = async (e) => {
    e.preventDefault();
    const newErrors = {};
    if (!userId) newErrors.userId = 'User ID is required'; 
    if (!userType) newErrors.userType = 'User Type is required';
    if (userType === 'Regional Network' && !region) newErrors.region = 'Region is required';
    if (userType === 'Member State') {
      if (!memberStateRegion) newErrors.memberState = 'Region is required';
      if (!memberState) newErrors.country = 'Member State is required';
    }

    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }

    try {
      const userData = {
        id: userId,
        isVeiw: permissions.viewReports,
        isEdit: permissions.editData,
        isDelete: permissions.deleteData,
        selectedOption: userType,
        regionalNetworkOption: userType === 'Regional Network' ? region : undefined,
        memberStateOption: userType === 'Member State' ? memberStateRegion : undefined,
        countryOption: userType === 'Member State' ? memberState : undefined,
      };

      const response = await apiService.post(`/api/update-user`, userData);

      if (response.status === 200) {  // Changed from response.statusText check
        setSnackbarMessage('User account updated successfully!');
        setSnackbarSeverity('success');
        setOpenSnackbar(true);

        setUserName('');
        setUserId('');
        setUserType('');
        setRegion('');
        setPermissions({
          viewReports: false,
          editData: false,
          deleteData: false,
        });
      } else {
        throw new Error('Failed to update user account');
      }
    } catch (error) {
      setSnackbarMessage(error.response?.data?.message || 'Error updating user account. Please try again.');
      setSnackbarSeverity('error');
      setOpenSnackbar(true);
    }
  };

  const handleUserChange = (event) => {
    const selectedUserName = event.target.value;
    const selectedUser = users.find(user => user.username === selectedUserName);
    setUserName(selectedUserName); 
    setUserId(selectedUser ? selectedUser._id : ''); 
  };

  const handlePermissionChange = (event) => {
    setPermissions({
      ...permissions,
      [event.target.name]: event.target.checked,
    });
  };

  return (
    <Container maxWidth="md" sx={{ paddingRight: '40px' }}>
      <Paper elevation={20} sx={{ p: 4, mt: 4 }}>
        <form onSubmit={handleSaveUser}>
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <FormControl fullWidth error={!!errors.userType}>
                <InputLabel>User Type*</InputLabel>
                <Select
                  value={userType}
                  onChange={(e) => setUserType(e.target.value)}
                  label="User Type"
                >
                  <MenuItem value="Member State">Member State</MenuItem>
                  <MenuItem value="Regional Network">Regional Network</MenuItem>
                </Select>
                {errors.userType && <p style={{ color: 'red' }}>{errors.userType}</p>}
              </FormControl>
            </Grid>

            {userType === 'Regional Network' && (
              <Grid item xs={12}>
                <FormControl fullWidth error={!!errors.region}>
                  <InputLabel>Region*</InputLabel>
                  <Select
                    value={region}
                    onChange={(e) => {
                      setRegion(e.target.value);
                      setUserName(''); // Clear selected user when region changes
                      setUserId('');
                    }}
                    label="Region"
                  >
                    <MenuItem value="COMESA">COMESA</MenuItem>
                    <MenuItem value="IGAD">IGAD</MenuItem>
                    <MenuItem value="SADC">SADC</MenuItem>
                    <MenuItem value="IOC">IOC</MenuItem>
                    <MenuItem value="EAC">EAC</MenuItem>
                  </Select>
                  {errors.region && <p style={{ color: 'red' }}>{errors.region}</p>}
                </FormControl>
              </Grid>
            )}

            {userType === 'Member State' && (
              <>
                <Grid item xs={12}>
                  <FormControl fullWidth error={!!errors.memberState}>
                    <InputLabel>Region*</InputLabel>
                    <Select
                      value={memberStateRegion}
                      onChange={(e) => setMemberStateRegion(e.target.value)}
                      label="Region"
                    >
                      <MenuItem value="COMESA">COMESA</MenuItem>
                      <MenuItem value="EAC">EAC</MenuItem>
                      <MenuItem value="IGAD">IGAD</MenuItem>
                      <MenuItem value="IOC">IOC</MenuItem>
                      <MenuItem value="SADC">SADC</MenuItem>
                    </Select>
                    {errors.memberState && <p style={{ color: 'red' }}>{errors.memberState}</p>}
                  </FormControl>
                </Grid>

                <Grid item xs={12}>
                  <FormControl fullWidth error={!!errors.country}>
                    <InputLabel>Member State*</InputLabel>
                    <Select
                      value={memberState}
                      onChange={(e) => setmemberState(e.target.value)}
                      label="Member State"
                    >
                      {availableStates.map((state) => (
                        <MenuItem key={state} value={state}>
                          {state}
                        </MenuItem>
                      ))}
                    </Select>
                    {errors.country && <p style={{ color: 'red' }}>{errors.country}</p>}
                  </FormControl>
                </Grid>
              </>
            )}

            <Grid item xs={12}>
              <FormControl fullWidth error={!!errors.userId}>
                <InputLabel>User Name*</InputLabel>
                <Select
                  value={userName || ""}
                  onChange={handleUserChange}
                  label="User Name"
                >
                  
                  {users && users.length > 0 ? (
                    users.map((user) => (
                      <MenuItem key={user._id} value={user.username}>
                        {user.username}
                      </MenuItem>
                    ))
                  ) 
                  : (
                    <MenuItem value="">
                      <em>No users available for selected criteria</em>
                    </MenuItem>
                  )
                  }
                </Select>
                {errors.userId && <p style={{ color: 'red' }}>{errors.userId}</p>}
              </FormControl>
            </Grid>

            <Grid item xs={12}>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={permissions.viewReports}
                    onChange={handlePermissionChange}
                    name="viewReports"
                  />
                }
                label="View Reports"
              />
              <FormControlLabel
                control={
                  <Checkbox
                    checked={permissions.editData}
                    onChange={handlePermissionChange}
                    name="editData"
                  />
                }
                label="Edit Data"
              />
              <FormControlLabel
                control={
                  <Checkbox
                    checked={permissions.deleteData}
                    onChange={handlePermissionChange}
                    name="deleteData"
                  />
                }
                label="Delete Data"
              />
            </Grid>

            <Grid item xs={12}>
              <Button variant="contained" color="primary" type="submit">
                Save User
              </Button>
            </Grid>
          </Grid>
        </form>
      </Paper>

      <Snackbar
        open={openSnackbar}
        autoHideDuration={6000}
        onClose={() => setOpenSnackbar(false)}
      >
        <Alert
          onClose={() => setOpenSnackbar(false)}
          severity={snackbarSeverity}
        >
          {snackbarMessage}
        </Alert>
      </Snackbar>
    </Container>
  );
};

export default UserManagement;
